import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit2, Trash2, DollarSign, Star } from 'lucide-react';
import { tariffsAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminTariffs = () => {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    messages_count: '',
    price: '',
    features: [],
    is_popular: false,
    is_active: true
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchTariffs();
  }, []);

  const fetchTariffs = async () => {
    try {
      const response = await tariffsAPI.getAllAdmin();
      setTariffs(response.data);
    } catch (error) {
      console.error('Error fetching tariffs:', error);
      toast.error('Ошибка загрузки тарифов');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      messages_count: '',
      price: '',
      features: [],
      is_popular: false,
      is_active: true
    });
    setNewFeature('');
    setEditingTariff(null);
  };

  const openDialog = (tariff = null) => {
    if (tariff) {
      setFormData({
        name: tariff.name,
        messages_count: tariff.messages_count.toString(),
        price: tariff.price.toString(),
        features: [...tariff.features],
        is_popular: tariff.is_popular,
        is_active: tariff.is_active
      });
      setEditingTariff(tariff);
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      messages_count: parseInt(formData.messages_count),
      price: parseFloat(formData.price)
    };

    try {
      if (editingTariff) {
        await tariffsAPI.update(editingTariff.id, submitData);
        toast.success('Тариф успешно обновлён');
      } else {
        await tariffsAPI.create(submitData);
        toast.success('Тариф успешно создан');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchTariffs();
    } catch (error) {
      console.error('Error saving tariff:', error);
      toast.error('Ошибка сохранения тарифа');
    }
  };

  const handleDelete = async (tariffId) => {
    if (confirm('Вы уверены, что хотите удалить этот тариф?')) {
      try {
        await tariffsAPI.delete(tariffId);
        toast.success('Тариф успешно удалён');
        fetchTariffs();
      } catch (error) {
        console.error('Error deleting tariff:', error);
        toast.error('Ошибка удаления тарифа');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление тарифами</h1>
          <p className="text-gray-400">Создание и редактирование тарифных планов</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-tariff-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Добавить тариф
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTariff ? 'Редактировать тариф' : 'Создать новый тариф'}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Заполните информацию о тарифном плане
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300">Название тарифа</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Базовый"
                    required
                    data-testid="tariff-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="messages_count" className="text-gray-300">Количество сообщений</Label>
                  <Input
                    id="messages_count"
                    type="number"
                    value={formData.messages_count}
                    onChange={(e) => handleInputChange('messages_count', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="100"
                    required
                    data-testid="tariff-messages-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price" className="text-gray-300">Цена (₽)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="299.00"
                    required
                    data-testid="tariff-price-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Цена за сообщение</Label>
                  <div className="p-3 bg-gray-700 rounded-md text-gray-300">
                    {formData.messages_count && formData.price 
                      ? `${(parseFloat(formData.price) / parseInt(formData.messages_count)).toFixed(2)} ₽`
                      : '0.00 ₽'
                    }
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-gray-300">Особенности тарифа</Label>
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="Добавить особенность"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    data-testid="add-feature-input"
                  />
                  <Button type="button" onClick={addFeature} variant="outline" className="border-gray-600">
                    Добавить
                  </Button>
                </div>
                
                {formData.features.length > 0 && (
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-700 p-2 rounded">
                        <span className="text-white">{feature}</span>
                        <Button
                          type="button"
                          onClick={() => removeFeature(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_popular"
                    checked={formData.is_popular}
                    onCheckedChange={(checked) => handleInputChange('is_popular', checked)}
                    data-testid="tariff-popular-switch"
                  />
                  <Label htmlFor="is_popular" className="text-gray-300">Популярный тариф</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                    data-testid="tariff-active-switch"
                  />
                  <Label htmlFor="is_active" className="text-gray-300">Активный</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-gray-600 text-gray-300"
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-tariff-btn"
                >
                  {editingTariff ? 'Обновить' : 'Создать'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tariffs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tariffs.map((tariff, index) => (
          <motion.div
            key={tariff.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Card className={`relative h-full ${
              tariff.is_popular 
                ? 'bg-gradient-to-b from-blue-900/50 to-gray-800 border-blue-500' 
                : 'bg-gray-800 border-gray-700'
            }`}>
              {tariff.is_popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white flex items-center">
                    <Star className="w-3 h-3 mr-1" />
                    Популярный
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-white text-xl">{tariff.name}</CardTitle>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog(tariff)}
                      className="text-blue-400 hover:text-blue-300"
                      data-testid={`edit-tariff-${tariff.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(tariff.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-tariff-${tariff.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <div className="text-3xl font-bold text-blue-400">{tariff.price}₽</div>
                  <div className="text-gray-400">{tariff.messages_count} сообщений</div>
                  <div className="text-sm text-gray-500">
                    {tariff.price_per_message?.toFixed(2)}₽ за сообщение
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Статус:</span>
                    <Badge className={tariff.is_active ? 'bg-green-600' : 'bg-gray-600'}>
                      {tariff.is_active ? 'Активен' : 'Неактивен'}
                    </Badge>
                  </div>
                  
                  {tariff.features && tariff.features.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-400 font-medium">Особенности:</div>
                      {tariff.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-300">
                          <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {tariffs.length === 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="text-center py-12">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Тарифы пока не созданы</p>
            <Button 
              onClick={() => openDialog()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Создать первый тариф
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminTariffs;